(function () {
  "use strict";

  const LOG_PREFIX = "[Frizbee Tracking]";

  let previousCart = null;
  let initialized = false;
  let processing = false;

  /**
   * Get current Shopify cart
   */
  async function getCart() {
    try {
      const response = await fetch("/cart.js", {
        method: "GET",
        credentials: "same-origin",
        headers: {
          Accept: "application/json"
        },
        cache: "no-store"
      });

      if (!response.ok) {
        throw new Error("Cart request failed: " + response.status);
      }

      return await response.json();
    } catch (error) {
      console.error(
        LOG_PREFIX,
        "Unable to read cart:",
        error
      );

      return null;
    }
  }

  /**
   * Convert cart item into tracking-friendly object
   */
  function normalizeItem(item) {
    if (!item) {
      return null;
    }

    return {
      variant_id: item.variant_id || item.id || null,
      product_id: item.product_id || null,
      product_title:
        item.product_title ||
        item.product_title ||
        item.title ||
        null,
      quantity: Number(item.quantity || 0),
      price: Number(
        item.final_price ||
        item.price ||
        0
      ) / 100,
      currency:
        item.presentment_price &&
        item.presentment_price.currency_code
          ? item.presentment_price.currency_code
          : null,
      sku: item.sku || null,
      handle: item.handle || null
    };
  }

  /**
   * Create lookup map by variant ID
   */
  function createItemMap(cart) {
    const map = {};

    if (!cart || !Array.isArray(cart.items)) {
      return map;
    }

    cart.items.forEach(function (item) {
      const key = String(
        item.variant_id || item.id
      );

      map[key] = item;
    });

    return map;
  }

  /**
   * Publish custom Shopify analytics event
   */
  function publishRemovedItem(item, removedQuantity) {
    const normalized = normalizeItem(item);

    if (!normalized) {
      return;
    }

    normalized.quantity = removedQuantity;

    /*
     * Shopify presentment currency.
     *
     * If Shopify does not expose it on the cart item,
     * fall back to the cart currency.
     */
    if (!normalized.currency && previousCart) {
      normalized.currency =
        previousCart.currency || "EUR";
    }

    console.log(
      LOG_PREFIX,
      "product removed:",
      normalized
    );

    try {
      if (
        window.Shopify &&
        Shopify.analytics &&
        typeof Shopify.analytics.publish === "function"
      ) {
        Shopify.analytics.publish(
          "frizbee:product_removed_from_cart",
          {
            variant_id: normalized.variant_id,
            product_id: normalized.product_id,
            product_title: normalized.product_title,
            quantity: normalized.quantity,
            presentment_price: normalized.price,
            currency: normalized.currency || "EUR",
            sku: normalized.sku,
            handle: normalized.handle
          }
        );
		
		
		// ==========================================
		// SEND REMOVE FROM CART TO GTM
		// ==========================================
		
		window.dataLayer = window.dataLayer || [];
		
		window.dataLayer.push({
		  event: "frizbee_remove_from_cart",
		
		  ecommerce: {
			currency: normalized.currency || "EUR",
		
			value:
			  Number(normalized.price || 0) *
			  Number(normalized.quantity || 1),
		
			items: [
			  {
				item_id: String(
				  normalized.variant_id ||
				  normalized.product_id ||
				  ""
				),
		
				item_name:
				  normalized.product_title || "",
		
				item_variant:
				  normalized.sku || "",
		
				price:
				  Number(normalized.price || 0),
		
				quantity:
				  Number(normalized.quantity || 1)
			  }
			]
		  }
		});
		
		console.log(
		  "[Frizbee Tracking] Sent remove_from_cart to GTM:",
		  {
			currency: normalized.currency || "EUR",
			value:
			  Number(normalized.price || 0) *
			  Number(normalized.quantity || 1)
		  }
		);

        console.log(
          LOG_PREFIX,
          "custom event published:",
          normalized
        );
      } else {
        console.warn(
          LOG_PREFIX,
          "Shopify.analytics.publish is unavailable."
        );
      }
    } catch (error) {
      console.error(
        LOG_PREFIX,
        "Failed to publish custom event:",
        error
      );
    }
  }

  /**
   * Compare previous cart with current cart.
   *
   * Detect:
   * 1. Complete removal
   * 2. Quantity decrease
   */
  function compareCarts(oldCart, newCart) {
    if (!oldCart || !newCart) {
      return;
    }

    const oldItems = createItemMap(oldCart);
    const newItems = createItemMap(newCart);

    Object.keys(oldItems).forEach(function (variantId) {
      const oldItem = oldItems[variantId];
      const newItem = newItems[variantId];

      const oldQuantity = Number(
        oldItem.quantity || 0
      );

      const newQuantity = newItem
        ? Number(newItem.quantity || 0)
        : 0;

      /*
       * Item completely removed
       */
      if (oldQuantity > 0 && newQuantity === 0) {
        publishRemovedItem(
          oldItem,
          oldQuantity
        );

        return;
      }

      /*
       * Quantity decreased
       */
      if (
        oldQuantity > newQuantity &&
        newQuantity >= 0
      ) {
        const removedQuantity =
          oldQuantity - newQuantity;

        publishRemovedItem(
          oldItem,
          removedQuantity
        );
      }
    });
  }

  /**
   * Check cart for changes
   */
  async function checkCart() {
    if (processing) {
      return;
    }

    processing = true;

    try {
      const currentCart = await getCart();

      if (!currentCart) {
        return;
      }

      /*
       * First successful request establishes baseline.
       */
      if (!initialized) {
        previousCart = currentCart;
        initialized = true;

        console.log(
          LOG_PREFIX,
          "Initial cart captured:",
          currentCart
        );

        return;
      }

      compareCarts(
        previousCart,
        currentCart
      );

      previousCart = currentCart;
    } finally {
      processing = false;
    }
  }

  /**
   * Detect Shopify AJAX cart changes.
   *
   * We listen for:
   * - fetch()
   * - XMLHttpRequest
   *
   * This is intentionally generic because the Frizbee
   * theme uses AJAX cart/update.js.
   */

  const originalFetch = window.fetch;

  window.fetch = async function () {
    const args = arguments;

    const response = await originalFetch.apply(
      this,
      args
    );

    try {
      const request =
        args && args.length
          ? args[0]
          : "";

      const url =
        typeof request === "string"
          ? request
          : request && request.url
          ? request.url
          : "";

      if (
        url.includes("/cart/add") ||
        url.includes("/cart/change") ||
        url.includes("/cart/update") ||
        url.includes("/cart/clear")
      ) {
        /*
         * Wait for Shopify/theme to finish updating
         * before reading /cart.js.
         */
        setTimeout(checkCart, 100);
        setTimeout(checkCart, 500);
      }
    } catch (error) {
      console.warn(
        LOG_PREFIX,
        "Fetch monitoring error:",
        error
      );
    }

    return response;
  };

  /**
   * XMLHttpRequest monitoring
   */
  const originalOpen =
    XMLHttpRequest.prototype.open;

  const originalSend =
    XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method,
    url
  ) {
    this.__frizbeeCartURL = url;

    return originalOpen.apply(
      this,
      arguments
    );
  };

  XMLHttpRequest.prototype.send = function () {
    const xhr = this;

    try {
      const url =
        xhr.__frizbeeCartURL || "";

      if (
        typeof url === "string" &&
        (
          url.includes("/cart/add") ||
          url.includes("/cart/change") ||
          url.includes("/cart/update") ||
          url.includes("/cart/clear")
        )
      ) {
        xhr.addEventListener(
          "load",
          function () {
            setTimeout(
              checkCart,
              100
            );

            setTimeout(
              checkCart,
              500
            );
          }
        );
      }
    } catch (error) {
      console.warn(
        LOG_PREFIX,
        "XHR monitoring error:",
        error
      );
    }

    return originalSend.apply(
      this,
      arguments
    );
  };

  /**
   * Initial cart snapshot
   */
  checkCart();

  /**
   * Safety net:
   * periodically compare the cart.
   *
   * This also catches cart changes made by
   * theme code that doesn't use fetch/XHR in
   * the expected way.
   */
  setInterval(
    checkCart,
    1000
  );

  console.log(
    LOG_PREFIX,
    "Cart tracking initialized."
  );
})();